// Phase 14 — global command palette (Cmd+K / Ctrl+K).
//
// Single-shot navigation + quick actions. Search uses cmdk's built-in
// scoring; group structure mirrors the sidebar's nav groups so the
// muscle memory transfers. Recent-actions persistence is intentionally
// scoped to localStorage so it survives a refresh per browser without
// needing a backend round-trip.

import { startTransition, useCallback, useEffect, useState } from 'react';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from '@/components/ui/command';
import {
  LayoutDashboard, Monitor, Camera, Clock, Activity, TrendingUp, Target,
  Globe, BarChart3, FileText, Sliders, Users, Settings, LineChart,
  CreditCard, Shield, Download, Smartphone, Eye, Plus, PlugZap,
} from 'lucide-react';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

interface CommandAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  shortcut?: string;
  /** When set, navigate to this href via the safe navigator. */
  to?: string;
  /** Free-text keywords that boost search hits (for the cmdk matcher). */
  keywords?: string;
  group: 'Navigation' | 'Quick actions';
  /** When set, mark this action as one a developer/admin uses to *make* something. */
  isAction?: boolean;
}

const ACTIONS: CommandAction[] = [
  // Navigation — mirrors Sidebar.tsx grouping
  { id: 'nav-dashboard',     group: 'Navigation', label: 'Dashboard',       icon: <LayoutDashboard className="h-4 w-4" />, to: '/' },
  { id: 'nav-live',          group: 'Navigation', label: 'Live Stream',     icon: <Eye className="h-4 w-4" />,             to: '/live-stream' },
  { id: 'nav-devices',       group: 'Navigation', label: 'Devices',         icon: <Monitor className="h-4 w-4" />,         to: '/devices' },
  { id: 'nav-screenshots',   group: 'Navigation', label: 'Screenshots',     icon: <Camera className="h-4 w-4" />,          to: '/screenshots' },
  { id: 'nav-timeline',      group: 'Navigation', label: 'Timeline',        icon: <Clock className="h-4 w-4" />,           to: '/timeline' },
  { id: 'nav-activity',      group: 'Navigation', label: 'Activity',        icon: <Activity className="h-4 w-4" />,        to: '/activity' },
  { id: 'nav-productivity',  group: 'Navigation', label: 'Productivity',    icon: <TrendingUp className="h-4 w-4" />,      to: '/productivity' },
  { id: 'nav-goals',         group: 'Navigation', label: 'Goals',           icon: <Target className="h-4 w-4" />,          to: '/goals' },
  { id: 'nav-goal-targets',  group: 'Navigation', label: 'Goal Targets',    icon: <Target className="h-4 w-4" />,          to: '/goals-platform' },
  { id: 'nav-apps-urls',     group: 'Navigation', label: 'Apps & URLs',     icon: <Globe className="h-4 w-4" />,           to: '/apps-urls' },
  { id: 'nav-sessions',      group: 'Navigation', label: 'Sessions',        icon: <BarChart3 className="h-4 w-4" />,       to: '/sessions' },
  { id: 'nav-reports',       group: 'Navigation', label: 'Reports',         icon: <FileText className="h-4 w-4" />,        to: '/reports' },
  { id: 'nav-rules',         group: 'Navigation', label: 'Rules',           icon: <Sliders className="h-4 w-4" />,         to: '/rules' },
  { id: 'nav-users',         group: 'Navigation', label: 'Users',           icon: <Users className="h-4 w-4" />,           to: '/users' },
  { id: 'nav-ops',           group: 'Navigation', label: 'Operations',      icon: <LineChart className="h-4 w-4" />,       to: '/ops' },
  { id: 'nav-billing',       group: 'Navigation', label: 'Billing',         icon: <CreditCard className="h-4 w-4" />,      to: '/billing' },
  { id: 'nav-audit',         group: 'Navigation', label: 'Audit Logs',      icon: <Shield className="h-4 w-4" />,          to: '/audit' },
  { id: 'nav-audit-explorer',group: 'Navigation', label: 'Audit Explorer',  icon: <Shield className="h-4 w-4" />,          to: '/audit-explorer' },
  { id: 'nav-agent-download',group: 'Navigation', label: 'Agent Download',  icon: <Download className="h-4 w-4" />,        to: '/agent-download' },
  { id: 'nav-mobile-app',    group: 'Navigation', label: 'Mobile App',      icon: <Smartphone className="h-4 w-4" />,      to: '/mobile-app' },

  // Quick actions — open builders/configs in one keystroke
  { id: 'action-new-rule',    group: 'Quick actions', label: 'Create a new rule…',   icon: <Plus className="h-4 w-4" />, isAction: true,
    to: '/rules?new=1', keywords: 'rule builder productivity threshold create new add' },
  { id: 'action-new-goal',    group: 'Quick actions', label: 'Set a new goal…',      icon: <Target className="h-4 w-4" />, isAction: true,
    to: '/goals-platform?new=1', keywords: 'goal target objective create new add' },
  { id: 'action-enroll',      group: 'Quick actions', label: 'Generate enrollment token…', icon: <PlugZap className="h-4 w-4" />, isAction: true,
    to: '/enrollment-tokens', keywords: 'enroll device token new agent' },
  { id: 'action-settings',    group: 'Quick actions', label: 'Open Settings',        icon: <Settings className="h-4 w-4" />,
    to: '/?settings=1', keywords: 'settings preferences config' },
  { id: 'action-ops',         group: 'Quick actions', label: 'Open Operations Center', icon: <LineChart className="h-4 w-4" />,
    to: '/ops', keywords: 'ops health diagnostics incidents' },
];

const RECENT_KEY = 'cmdk:recent:v1';
const MAX_RECENT = 5;

function loadRecent(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
  catch { return []; }
}
function saveRecent(ids: string[]) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, MAX_RECENT))); }
  catch {}
}

// Phase 15 hotfix follow-up — the palette is the single most
// "navigate to a lazy route on a click" surface in the app. Even with
// startTransition wrapping, the very FIRST navigation to a route can
// still flash a fallback loader while the chunk downloads. We pre-fetch
// every linked chunk on browser idle (after the user authenticates and
// the dashboard has settled) so subsequent palette picks are
// effectively instant. The dynamic-import side effect populates the
// browser's module cache; we ignore the resolved value.
const ROUTE_PREFETCHERS: Record<string, () => Promise<unknown>> = {
  '/':                () => import('@/pages/Dashboard'),
  '/live-stream':     () => import('@/pages/LiveStream'),
  '/devices':         () => import('@/pages/Devices'),
  '/screenshots':     () => import('@/pages/Screenshots'),
  '/timeline':        () => import('@/pages/Timeline'),
  '/activity':        () => import('@/pages/Activity'),
  '/productivity':    () => import('@/pages/Productivity'),
  '/goals':           () => import('@/pages/Goals'),
  '/goals-platform':  () => import('@/pages/GoalsPlatform'),
  '/apps-urls':       () => import('@/pages/AppsUrls'),
  '/sessions':        () => import('@/pages/Sessions'),
  '/reports':         () => import('@/pages/Reports'),
  '/rules':           () => import('@/pages/Rules'),
  '/users':           () => import('@/pages/Users'),
  '/ops':             () => import('@/pages/Ops'),
  '/billing':         () => import('@/pages/Billing'),
  '/audit':           () => import('@/pages/AuditLogs'),
  '/audit-explorer':  () => import('@/pages/AuditExplorer'),
  '/agent-download':  () => import('@/pages/AgentDownload'),
  '/mobile-app':      () => import('@/pages/MobileApp'),
  '/enrollment-tokens': () => import('@/pages/EnrollmentTokens'),
};

function schedulePrefetch(cb: () => void) {
  const ric: any = (window as any).requestIdleCallback;
  if (typeof ric === 'function') ric(cb, { timeout: 4000 });
  else setTimeout(cb, 1500);
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>(() => loadRecent());
  const navigate = useSafeNavigate();

  // Cmd+K / Ctrl+K to toggle, Esc to close (handled by Dialog primitive).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Prefetch all known route chunks on first idle so a palette pick
  // never has to wait on a fresh chunk load (which is what made the
  // 426 race possible even after startTransition wrapping).
  useEffect(() => {
    schedulePrefetch(() => {
      for (const path of Object.keys(ROUTE_PREFETCHERS)) {
        try { ROUTE_PREFETCHERS[path](); } catch { /* swallow */ }
      }
    });
  }, []);

  const run = useCallback((action: CommandAction) => {
    // Phase 15 hotfix — cmdk fires onSelect inside a synchronous click
    // dispatch. Closing the dialog AND navigating to a lazy route in
    // the same flush trips React error 426 ("Suspense fired by
    // synchronous input"), exactly the failure we fought in Phase
    // 12.3. Wrapping both in startTransition marks the whole
    // close+navigate as a non-urgent transition so React waits for
    // the chunk instead of replacing the UI with a loader.
    startTransition(() => {
      setOpen(false);
      if (action.to) navigate(action.to);
    });
    // recent-list mutation stays synchronous — it's just localStorage,
    // it doesn't trigger Suspense.
    const next = [action.id, ...recent.filter((id) => id !== action.id)];
    setRecent(next);
    saveRecent(next);
  }, [navigate, recent]);

  const recentActions = recent
    .map((id) => ACTIONS.find((a) => a.id === id))
    .filter((a): a is CommandAction => !!a);

  const grouped = ACTIONS.reduce<Record<string, CommandAction[]>>((acc, a) => {
    (acc[a.group] ||= []).push(a);
    return acc;
  }, {});

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages, settings, actions…" data-testid="cmdk-input" />
      <CommandList data-testid="cmdk-list">
        <CommandEmpty>No results.</CommandEmpty>

        {recentActions.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentActions.map((a) => (
                <CommandItem key={`recent-${a.id}`} value={a.label} onSelect={() => run(a)}>
                  {a.icon}
                  <span className="ml-2">{a.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {Object.entries(grouped).map(([group, items], idx, all) => (
          <div key={group}>
            <CommandGroup heading={group}>
              {items.map((a) => (
                <CommandItem
                  key={a.id}
                  value={a.label + ' ' + (a.keywords || '')}
                  onSelect={() => run(a)}
                >
                  {a.icon}
                  <span className="ml-2">{a.label}</span>
                  {a.shortcut && <CommandShortcut>{a.shortcut}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
            {idx < all.length - 1 && <CommandSeparator />}
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
